import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  User, Buildings, Chalkboard, CheckCircle, XCircle,
  Warning, ShieldCheck, Clock
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gm-class-training/artifacts/1szu61lb_Logo%20G%26M.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const getEstado = (t) => {
  // Certificate validity takes priority
  if (t.has_certificate && !t.is_expired) return { label: 'Vigente', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
  if (t.has_certificate && t.is_expired) return { label: 'Vencido', icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' };
  // No certificate: reflect the evaluation result (consistent with the RUT search view)
  const approved = t.status === 'approved' || t.status === 'passed' || (t.score !== null && t.score !== undefined && t.score >= 60);
  const failed = t.status === 'failed' || t.status === 'rejected' || (t.score !== null && t.score !== undefined && t.score < 60);
  if (approved) return { label: 'Aprobado', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
  if (failed) return { label: 'Reprobado', icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' };
  return { label: 'En Curso', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' };
};

export default function CompetenciasPage() {
  const { workerId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/competencias/${workerId}`);
        if (!res.ok) throw new Error();
        setData(await res.json());
      } catch { setError(true); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [workerId]);

  const estados = (data?.trainings || []).map(getEstado);
  const certified = estados.filter(e => e.label === 'Vigente' || e.label === 'Aprobado').length;
  const expired = estados.filter(e => e.label === 'Vencido' || e.label === 'Reprobado').length;
  const inProgress = estados.filter(e => e.label === 'En Curso').length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#2B4B8C]/5 to-[#f8fafc]" data-testid="competencias-page">
      <header className="bg-white border-b border-[#2B4B8C]/10 shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-center">
          <img src={LOGO_URL} alt="G&M OTEC" className="h-9 object-contain" />
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {loading && (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
          </div>
        )}

        {error && (
          <Card className="bg-white border-red-200 shadow-lg overflow-hidden">
            <div className="bg-red-500 px-4 py-3 flex items-center gap-2.5">
              <XCircle size={22} className="text-white" weight="fill" />
              <p className="text-white font-semibold text-sm">Trabajador no encontrado</p>
            </div>
            <CardContent className="p-6 text-center text-[#2B4B8C]/60 text-sm">
              El codigo QR no corresponde a un trabajador registrado
            </CardContent>
          </Card>
        )}

        {data && (
          <>
            <div className="text-center mb-5">
              <div className="w-12 h-12 rounded-full bg-[#2B4B8C]/10 flex items-center justify-center mx-auto mb-3">
                <ShieldCheck size={24} className="text-[#2B4B8C]" />
              </div>
              <h1 className="text-xl font-bold text-[#2B4B8C]">Competencias del Trabajador</h1>
            </div>

            {/* Worker Card */}
            <Card className="bg-white border-[#2B4B8C]/10 shadow-sm mb-4 overflow-hidden">
              <div className="bg-[#2B4B8C] px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <User size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{data.worker_name}</p>
                    <p className="text-white/70 text-xs">RUT: {data.worker_rut}</p>
                  </div>
                </div>
              </div>
              <CardContent className="p-3 flex items-center gap-2">
                <Buildings size={14} className="text-[#2B4B8C]/40" />
                <span className="text-sm text-[#2B4B8C]/70">{data.company_name || 'Sin empresa asignada'}</span>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                <p className="text-2xl font-bold text-green-600">{certified}</p>
                <p className="text-[10px] text-green-600/70 font-medium uppercase">Vigentes</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
                <p className="text-2xl font-bold text-amber-600">{inProgress}</p>
                <p className="text-[10px] text-amber-600/70 font-medium uppercase">En Curso</p>
              </div>
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
                <p className="text-2xl font-bold text-red-500">{expired}</p>
                <p className="text-[10px] text-red-500/70 font-medium uppercase">Vencidos</p>
              </div>
            </div>

            {/* Trainings List */}
            <div className="space-y-2">
              {data.trainings.map((t, i) => {
                const estado = getEstado(t);
                const Icon = estado.icon;
                return (
                  <Card key={i} className={`bg-white shadow-sm overflow-hidden border ${estado.border}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg ${estado.bg} flex items-center justify-center shrink-0`}>
                        <Icon size={18} className={estado.color} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#2B4B8C] truncate">{t.training_name}</p>
                        <p className="text-xs text-[#2B4B8C]/50">{data.company_name || '-'}</p>
                        <p className="text-[11px] text-[#2B4B8C]/40 mt-0.5">
                          Vigencia: {t.certificate_expiry ? format(new Date(t.certificate_expiry), "dd/MM/yyyy") : 'Sin fecha'}
                        </p>
                      </div>
                      <Badge className={`${estado.bg} ${estado.color} border-0 text-[10px] px-2 py-0.5 shrink-0 font-semibold`}>
                        {estado.label}
                      </Badge>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {data.trainings.length === 0 && (
              <Card className="bg-white border-[#2B4B8C]/10 shadow-sm">
                <CardContent className="py-12 text-center text-[#2B4B8C]/40">
                  <Chalkboard size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Sin capacitaciones registradas</p>
                </CardContent>
              </Card>
            )}

            <p className="mt-6 text-center text-xs text-[#2B4B8C]/35">
              Informacion verificada por G&M OTEC
            </p>
          </>
        )}
      </main>
    </div>
  );
}
